/** Canvas geometry from a static render, for hit testing, handles and the commands' Geo interface. */
import type { Scene } from '@prism-graphics/scene-schema';
import { apply, invert, type ElementGeometry, type Matrix, type Point, type StaticFrame } from '@prism-graphics/runtime/static';
import type { Geo } from '../model/commands.ts';
import { findElement, isContainer } from '../model/tree.ts';

export function geoFromFrame(scene: Scene, frame: StaticFrame): Geo {
  const g = frame.geometry;
  return {
    topLeft(id) {
      const e = g.get(id);
      const el = findElement(scene, id);
      if (!e || !el) return undefined;
      const t = el.transform;
      if (t.rotation === 0 && t.scaleX === 1 && t.scaleY === 1) return apply(e.matrix, { x: 0, y: 0 });
      const c = apply(e.matrix, { x: e.box.width / 2, y: e.box.height / 2 });
      return { x: c.x - e.box.width / 2, y: c.y - e.box.height / 2 };
    },
    contentOrigin(id) {
      if (id === null) return { x: 0, y: 0 };
      const e = g.get(id);
      const el = findElement(scene, id);
      if (!e || !el) return { x: 0, y: 0 };
      const border = el.type === 'group' ? (el.style.stroke?.width ?? 0) : 0;
      return apply(e.matrix, { x: border, y: border });
    },
    bounds(id) {
      return g.get(id)?.bounds;
    },
    size(id) {
      const e = g.get(id);
      return e ? { width: e.box.width, height: e.box.height } : undefined;
    },
    contentSize(id) {
      if (id === null) return { width: scene.canvas.width, height: scene.canvas.height };
      const e = g.get(id);
      const el = findElement(scene, id);
      if (!e || !el) return { width: scene.canvas.width, height: scene.canvas.height };
      const border = el.type === 'group' ? (el.style.stroke?.width ?? 0) : 0;
      return { width: e.box.width - 2 * border, height: e.box.height - 2 * border };
    },
  };
}

export function inQuad(p: Point, q: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
    const a = q[i]!;
    const b = q[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** The chain of ids (top level → deepest) of the front-most element under `p`, skipping hidden and locked ones. */
export function hitTest(scene: Scene, geometry: ReadonlyMap<string, ElementGeometry>, p: Point): string[] | undefined {
  const visit = (els: Scene['elements'], trail: string[]): string[] | undefined => {
    for (let i = els.length - 1; i >= 0; i--) {
      const el = els[i]!;
      const g = geometry.get(el.id);
      if (!g?.visible || el.locked) continue;
      if (isContainer(el)) {
        const r = visit(el.children, [...trail, el.id]);
        if (r) return r;
      }
      if (inQuad(p, g.quad)) return [...trail, el.id];
    }
    return undefined;
  };
  return visit(scene.elements, []);
}

/** A screen-space delta expressed along the element's own axes (for resizing rotated elements). */
export function toLocalDelta(m: Matrix, d: Point): Point {
  const inv = invert([m[0], m[1], m[2], m[3], 0, 0]);
  return apply(inv, d);
}
